import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useToast } from '@/components/ui/use-toast';
import { Check, X, Pencil, Utensils, MapPin, Globe, Link, ImageIcon } from 'lucide-react';
import { useUpdateBurger } from '@/data/mutations/useUpdateBurger';
import { useUpdateProducer } from '@/data/mutations/useUpdateProducer';
import { useUpdateProposalStatus } from '@/data/mutations/useProposalMutations';
import { logError } from "@/lib/logError";
import { safeExternalUrl } from "@/lib/safeUrl";

type FieldType = 'description' | 'website_url' | 'instagram_url' | 'image';

interface ReviewProposalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposalId: string;
  entityType: 'burger' | 'producer';
  entityId: string;
  entitySlug: string;
  entityName: string;
  proposedValue: string;
  fieldType?: FieldType;
  proposerId: string;
  proposerName: string | null;
  proposerAvatarUrl: string | null;
  onActionComplete?: () => void;
  // Legacy prop for backward compatibility
  proposedDescription?: string;
}

const fieldLabels: Record<FieldType, string> = {
  description: 'description',
  website_url: 'website',
  instagram_url: 'Instagram',
  image: 'image',
};

const fieldDbColumns: Record<FieldType, Record<string, string>> = {
  description: { burger: 'description', producer: 'description' },
  website_url: { burger: 'website_url', producer: 'website_url' },
  instagram_url: { burger: 'instagram_url', producer: 'instagram_url' },
  image: { burger: 'canonical_image_url', producer: 'logo_url' },
};

function getFieldIcon(fieldType: FieldType) {
  switch (fieldType) {
    case 'website_url':
      return Globe;
    case 'instagram_url':
      return Link;
    case 'image':
      return ImageIcon;
    default:
      return null;
  }
}

export function ReviewProposalDialog({
  open,
  onOpenChange,
  proposalId,
  entityType,
  entityId,
  entitySlug,
  entityName,
  proposedValue,
  fieldType = 'description',
  proposerId,
  proposerName,
  proposerAvatarUrl,
  onActionComplete,
  proposedDescription
}: ReviewProposalDialogProps) {
  // Support legacy prop
  const value = proposedValue || proposedDescription || '';
  const [editedValue, setEditedValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const updateBurger = useUpdateBurger();
  const updateProducer = useUpdateProducer();
  const updateProposalStatus = useUpdateProposalStatus();

  const fieldLabel = fieldLabels[fieldType];
  const isUrl = fieldType === 'website_url' || fieldType === 'instagram_url';
  const isImage = fieldType === 'image';

  const handleAccept = async (useEdited: boolean = false) => {
    setSubmitting(true);
    try {
      let valueToUse = useEdited ? editedValue.trim() : value;
      const dbColumn = fieldDbColumns[fieldType][entityType];

      // Enforce http(s) scheme for any URL / image field before persisting,
      // so a proposer cannot smuggle javascript:/data:/other schemes past the UI.
      if (isUrl || isImage) {
        const safe = safeExternalUrl(valueToUse);
        if (!safe) {
          toast({
            title: "Invalid URL",
            description: "Only http(s) URLs can be accepted for this field.",
            variant: "destructive",
          });
          setSubmitting(false);
          return;
        }
        valueToUse = safe;
      }

      // Update the entity's field
      if (entityType === 'burger') {
        await updateBurger.mutateAsync({ id: entityId, patch: { [dbColumn]: valueToUse } as any });
      } else {
        await updateProducer.mutateAsync({ id: entityId, patch: { [dbColumn]: valueToUse } as any });
      }

      // Mark proposal as accepted. The `trg_notify_proposal_result` DB trigger
      // fans out the in-app notification, email and push to the proposer —
      // keep this client-side only, never invoke the edge function directly.
      await updateProposalStatus.mutateAsync({ id: proposalId, status: 'accepted' });


      
      toast({
        title: `${fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1)} updated!`,
        description: `The ${entityType} ${fieldLabel} has been updated successfully.`
      });
      
      onOpenChange(false);
      onActionComplete?.();
    } catch (error: any) {
      logError(error, { where: "Error accepting proposal" });
      toast({
        title: "Failed to update",
        description: error.message || "Please try again",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      // The `trg_notify_proposal_result` DB trigger fans out the in-app
      // notification, email and push once the status flips.
      await updateProposalStatus.mutateAsync({ id: proposalId, status: 'rejected' });


      
      toast({
        title: "Proposal declined",
        description: `The ${fieldLabel} proposal has been declined.`
      });
      
      onOpenChange(false);
      onActionComplete?.();
    } catch (error: any) {
      logError(error, { where: "Error rejecting proposal" });
      toast({
        title: "Failed to decline",
        description: error.message || "Please try again",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isBurger = entityType === 'burger';
  const EntityIcon = isBurger ? Utensils : MapPin;
  const FieldIcon = getFieldIcon(fieldType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EntityIcon className="h-5 w-5 text-primary" />
            Review {fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1)} Proposal
          </DialogTitle>
          <DialogDescription>
            Review the proposed {fieldLabel} for <strong>{entityName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Proposer info */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <Avatar className="h-10 w-10">
              {proposerAvatarUrl && <AvatarImage src={proposerAvatarUrl} />}
              <AvatarFallback className="bg-primary/10 text-primary text-sm">
                {getInitials(proposerName)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-sm">{proposerName || 'Someone'}</p>
              <p className="text-xs text-muted-foreground">suggested this {fieldLabel}</p>
            </div>
          </div>

          {/* Proposed value */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {FieldIcon && <FieldIcon className="h-4 w-4 text-muted-foreground" />}
                <label className="text-sm font-medium">Proposed {fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1)}</label>
              </div>
              {!isEditing && !isImage && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="h-8 px-2"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            
            {isImage ? (
              <div className="p-3 bg-muted/30 rounded-lg border">
                <img src={value} alt="Proposed burger image" className="w-full h-48 rounded-lg object-cover bg-muted" />
              </div>
            ) : isEditing ? (
              <div className="space-y-2">
                {isUrl ? (
                  <Input
                    value={editedValue}
                    onChange={(e) => setEditedValue(e.target.value)}
                    type={fieldType === 'website_url' ? 'url' : 'text'}
                  />
                ) : (
                  <>
                    <Textarea
                      value={editedValue}
                      onChange={(e) => setEditedValue(e.target.value)}
                      className="min-h-[120px]"
                      maxLength={500}
                    />
                    <span className="text-xs text-muted-foreground">
                      {editedValue.length}/500 characters
                    </span>
                  </>
                )}
                <div className="flex items-center justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditedValue(value);
                      setIsEditing(false);
                    }}
                    className="h-7 text-xs"
                  >
                    Cancel edit
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-muted/30 rounded-lg border">
                {isUrl ? (
                  <a
                    href={fieldType === 'instagram_url'
                      ? `https://instagram.com/${value.replace('@', '')}`
                      : (value.startsWith('http') ? value : `https://${value}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:text-primary-hover underline-offset-2 hover:underline transition-colors break-all"
                  >
                    {value}
                  </a>
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{value}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleReject}
            disabled={submitting}
            className="flex-1 sm:flex-none"
          >
            <X className="h-4 w-4 mr-2" />
            Decline
          </Button>
          <Button
            onClick={() => handleAccept(isEditing && editedValue !== value)}
            disabled={submitting || (isEditing && !editedValue.trim())}
            className="flex-1 sm:flex-none"
          >
            <Check className="h-4 w-4 mr-2" />
            {isEditing && editedValue !== value 
              ? "Accept with edits" 
              : "Accept"
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}